import axios from 'axios';
export var ContentType;
(function (ContentType) {
    ContentType["Json"] = "application/json";
    ContentType["FormData"] = "multipart/form-data";
    ContentType["UrlEncoded"] = "application/x-www-form-urlencoded";
    ContentType["Text"] = "text/plain";
})(ContentType || (ContentType = {}));
export class HttpClient {
    instance;
    securityData = null;
    securityWorker;
    secure;
    format;
    constructor({ securityWorker, secure, format, ...axiosConfig } = {}) {
        this.instance = axios.create({ ...axiosConfig, baseURL: axiosConfig.baseURL || '' });
        this.secure = secure;
        this.format = format;
        this.securityWorker = securityWorker;
    }
    setSecurityData = (data) => {
        this.securityData = data;
    };
    mergeRequestParams(params1, params2) {
        const method = params1.method || (params2 && params2.method);
        return {
            ...this.instance.defaults,
            ...params1,
            ...(params2 || {}),
            headers: {
                ...((method && this.instance.defaults.headers[method.toLowerCase()]) || {}),
                ...(params1.headers || {}),
                ...((params2 && params2.headers) || {}),
            },
        };
    }
    stringifyFormItem(formItem) {
        if (typeof formItem === 'object' && formItem !== null) {
            return JSON.stringify(formItem);
        }
        else {
            return `${formItem}`;
        }
    }
    createFormData(input) {
        if (input instanceof FormData) {
            return input;
        }
        return Object.keys(input || {}).reduce((formData, key) => {
            const property = input[key];
            const propertyContent = property instanceof Array ? property : [property];
            for (const formItem of propertyContent) {
                const isFileType = formItem instanceof Blob || formItem instanceof File;
                formData.append(key, isFileType ? formItem : this.stringifyFormItem(formItem));
            }
            return formData;
        }, new FormData());
    }
    request = async ({ secure, path, type, query, format, body, ...params }) => {
        const secureParams = ((typeof secure === 'boolean' ? secure : this.secure) &&
            this.securityWorker &&
            (await this.securityWorker(this.securityData))) ||
            {};
        const requestParams = this.mergeRequestParams(params, secureParams);
        const responseFormat = format || this.format || undefined;
        if (type === ContentType.FormData && body && body !== null && typeof body === 'object') {
            body = this.createFormData(body);
        }
        if (type === ContentType.Text && body && body !== null && typeof body !== 'string') {
            body = JSON.stringify(body);
        }
        return this.instance.request({
            ...requestParams,
            headers: {
                ...(requestParams.headers || {}),
                ...(type ? { 'Content-Type': type } : {}),
            },
            params: query,
            responseType: responseFormat,
            data: body,
            url: path,
        });
    };
}
export class Api extends HttpClient {
    api = {
        projectsControllerCreate: (data, params = {}) => this.request({
            path: `/api/projects`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        projectsControllerFind: (params = {}) => this.request({
            path: `/api/projects`,
            method: 'GET',
            format: 'json',
            ...params,
        }),
        projectsControllerFindById: (projectId, params = {}) => this.request({
            path: `/api/projects/${projectId}`,
            method: 'GET',
            format: 'json',
            ...params,
        }),
        projectsControllerDelete: (projectId, params = {}) => this.request({
            path: `/api/projects/${projectId}`,
            method: 'DELETE',
            ...params,
        }),
        projectsControllerCreateReview: (id, data, params = {}) => this.request({
            path: `/api/projects/${id}/review`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        projectsControllerUpdateContactForComplaintEmail: (projectId, data, params = {}) => this.request({
            path: `/api/projects/${projectId}/contact-for-complaint-email`,
            method: 'PATCH',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        projectsControllerUpdateName: (projectId, data, params = {}) => this.request({
            path: `/api/projects/${projectId}/name`,
            method: 'PATCH',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        projectsControllerUpdateCslStyle: (projectId, data, params = {}) => this.request({
            path: `/api/projects/${projectId}/csl-style`,
            method: 'PATCH',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        projectsControllerAddMember: (projectId, userId, params = {}) => this.request({
            path: `/api/projects/${projectId}/users/${userId}`,
            method: 'PUT',
            ...params,
        }),
        projectsControllerRemoveMember: (projectId, userId, params = {}) => this.request({
            path: `/api/projects/${projectId}/users/${userId}`,
            method: 'DELETE',
            ...params,
        }),
        reviewsControllerFindById: (reviewId, query, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}`,
            method: 'GET',
            query: query,
            format: 'json',
            ...params,
        }),
        reviewsControllerSubmitComplaint: (reviewId, studyId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/submit-complaint`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        reviewsControllerMarkStudyAsDuplicate: (reviewId, studyId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/mark-as-duplicate`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerMarkStudyAsNotDuplicate: (reviewId, studyId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/mark-as-not-duplicate`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerSetStudyTitleAndAbstractScreening: (reviewId, studyId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/title-and-abstract-screening`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerClearStudyTitleAndAbstractScreening: (reviewId, studyId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/title-and-abstract-screening`,
            method: 'DELETE',
            ...params,
        }),
        reviewsControllerSetStudyFullTextScreening: (reviewId, studyId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/full-text-screening`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerClearStudyFullTextScreening: (reviewId, studyId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/full-text-screening`,
            method: 'DELETE',
            ...params,
        }),
        reviewsControllerDeleteSearch: (reviewId, searchId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/search/${searchId}`,
            method: 'DELETE',
            ...params,
        }),
        reviewsControllerPlanScreening: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/plan-screening`,
            method: 'PUT',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerPlanLocking: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/plan-locking`,
            method: 'PUT',
            ...params,
        }),
        reviewsControllerPlanUnlocking: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/plan-unlocking`,
            method: 'PUT',
            ...params,
        }),
        reviewsControllerAddFullTextCriterion: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/add-full-text-criterion`,
            method: 'PUT',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerAddInclusionluCriterion: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/add-inclusion-criterion`,
            method: 'PUT',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerAddPicoInclusionluCriterion: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/add-pico-inclusion-criterion`,
            method: 'PUT',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerDeleteFullTextCriterion: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/delete-full-text-criterion`,
            method: 'PUT',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerDeleteInclusionCriterion: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/delete-inclusion-criterion`,
            method: 'PUT',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerDeletePicoInclusionCriterion: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/delete-pico-inclusion-criterion`,
            method: 'PUT',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerAddTitleAndAbstractCriterion: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/add-title-and-abstract-criterion`,
            method: 'PUT',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerDeleteTitleAndAbstractCriterion: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/delete-title-and-abstract-criterion`,
            method: 'PUT',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerEnableTitleAndAbstractScreening: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/enable-title-and-abstract-screening`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerDisableTitleAndAbstractScreening: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/disable-title-and-abstract-screening`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerFindProject: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/project`,
            method: 'GET',
            format: 'json',
            ...params,
        }),
        reviewsControllerFavoriteStudy: (reviewId, studyId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/favorite`,
            method: 'POST',
            ...params,
        }),
        reviewsControllerUnfavoriteStudy: (reviewId, studyId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/unfavorite`,
            method: 'POST',
            ...params,
        }),
        reviewsControllerUploadStudyPdfFile: (reviewId, studyId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/upload-pdf`,
            method: 'POST',
            body: data,
            type: ContentType.FormData,
            format: 'json',
            ...params,
        }),
        reviewsControllerGetStudyPdfFile: (reviewId, studyId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/pdf`,
            method: 'GET',
            ...params,
        }),
        reviewsControllerExportStudiesZip: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/zip`,
            method: 'GET',
            ...params,
        }),
        reviewsControllerDeleteStudyPdfFile: (reviewId, studyId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/pdf-file`,
            method: 'DELETE',
            ...params,
        }),
        reviewsControllerEditStudyAbstract: (reviewId, studyId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/abstract`,
            method: 'PATCH',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerRemoveImportSourceFromPlan: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/import-plan/import-source`,
            method: 'DELETE',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerEnableStudiesAppraisalImdrfMdce2019: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/enable-studies-appraisal`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerDisableStudiesAppraisal: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/disable-studies-appraisal`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerEnableStudiesAppraisalOxfordLevelOfEvidence: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/enable-studies-appraisal-oxford-level-of-evidence`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerEnableStudiesAppraisalPeerReviewStatus: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/enable-studies-appraisal-peer-review-status`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerDisableStudiesAppraisalPeerReviewStatus: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/disable-studies-appraisal-peer-review-status`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerDisableStudiesAppraisalOxfordLevelOfEvidence: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/disable-studies-appraisal-oxford-level-of-evidence`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerAppraiseStudy: (reviewId, studyId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/appraise`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerSetOxfordLevelOfEvidenceStudy: (reviewId, studyId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/setOxfordLevelOfEvidence`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerSetPeerReviewStatus: (reviewId, studyId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/setPeerReviewStatus`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerClearStudyOxfordLevelOfEvidence: (reviewId, studyId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/resetOxfordLevelOfEvidence`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerClearStudyPeerReviewStatus: (reviewId, studyId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/clearStudyPeerReviewStatus`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerClearStudyAppraisal: (reviewId, studyId, criterionId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/clearAppraisal/${criterionId}`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerUpdateStudy: (reviewId, studyId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/update`,
            method: 'PATCH',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerAddAttributeStructure: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/add-data-extraction-attribute`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        reviewsControllerUpdateSynthesisPlanSummary: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/update-synthesis-plan-summary`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerUpdateSearchStrategySummary: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/update-search-strategy-summary`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerImportSearch: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/import-search`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        reviewsControllerAddArticlesToSearch: (reviewId, searchId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/searches/${searchId}/add-studies`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerImportCitationSearch: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/import-citation-search`,
            method: 'POST',
            body: data,
            type: ContentType.FormData,
            ...params,
        }),
        reviewsControllerDeleteAttributeStructure: (reviewId, attributeStructureId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/${attributeStructureId}/attribute-structure`,
            method: 'DELETE',
            ...params,
        }),
        reviewsControllerEditAttributeStructure: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/edit-attribute-structure`,
            method: 'PUT',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerFinalizeReviewItemSynthesisAttribute: (reviewId, studyId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/finalizeReviewItemSynthesis`,
            method: 'PATCH',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerUnfinalizeReviewItemSynthesisAttribute: (reviewId, studyId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/unfinalizeReviewItemSynthesis`,
            method: 'PATCH',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerClearAttributeAiSuggestion: (reviewId, studyId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/studies/${studyId}/clearAttributeAiSuggestion`,
            method: 'PATCH',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerAddCustomImportSource: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/plan/custom-import-source`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        reviewsControllerAddAppraisalCriteria: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/add-appraisal-criteria`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerDeleteAppraisalCriterionAnswer: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/delete-appraisal-criterion-answer`,
            method: 'PUT',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerLockReview: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/lock`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerUnlockReview: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/unlock`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerEnablePicoInclusionCriteria: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/enablePicoInclusionCriteria`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerDisablePicoInclusionCriteria: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/disablePicoInclusionCriteriaUsecase`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerEnableEvidenceLevelInclusionCriteria: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/enableEvidenceLevelInclusionCriteria`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerDisableEvidenceLevelInclusionCriteria: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/disableEvidenceLevelInclusionCriteria`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerEnableInclusionTimeLimit: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/enableInclusionTimeLimit`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerDisableInclusionTimeLimit: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/disableInclusionTimeLimit`,
            method: 'PATCH',
            ...params,
        }),
        reviewsControllerUpdateInclusionCriteriaEvidenceLevel: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/updateInclusionCriteriaEvidenceLevelUsecase`,
            method: 'PUT',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerUpdateInclusionCriteriaTimeLimit: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/updateInclusionCriteriaTimeLimit`,
            method: 'PUT',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerCreateEvaluator: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/create-evaluator`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        reviewsControllerDeleteEvaluator: (reviewId, evaluatorId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/evaluator/${evaluatorId}`,
            method: 'DELETE',
            ...params,
        }),
        reviewsControllerUpdateEvaluator: (reviewId, evaluatorId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/evaluator/${evaluatorId}`,
            method: 'PUT',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerSynthesizeDataExtractionAttribute: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/synthesize-data-extraction-attribute`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        reviewsControllerCreateReviewUpdate: (reviewSeriesId, params = {}) => this.request({
            path: `/api/Reviews/${reviewSeriesId}/review-update`,
            method: 'POST',
            format: 'json',
            ...params,
        }),
        reviewsControllerCanLockReview: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/canLockReview`,
            method: 'GET',
            format: 'json',
            ...params,
        }),
        reviewsControllerScheduleAutoScreening: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/schedule-auto-screening`,
            method: 'POST',
            format: 'json',
            ...params,
        }),
        reviewsControllerScheduleAutoExtraction: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/schedule-auto-extraction`,
            method: 'POST',
            format: 'json',
            ...params,
        }),
        reviewsControllerFinalizeAttributeSynthesis: (reviewId, attributeId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/finalize-attribute-synthesis/${attributeId}`,
            method: 'POST',
            ...params,
        }),
        reviewsControllerUnfinalizeAttributeSynthesis: (reviewId, attributeId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/unfinalize-attribute-synthesis/${attributeId}`,
            method: 'POST',
            ...params,
        }),
        reviewsControllerFinalizeAttributeSynthesisDelta: (reviewId, attributeId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/finalize-attribute-synthesis-delta/${attributeId}`,
            method: 'POST',
            ...params,
        }),
        reviewsControllerUnfinalizeAttributeSynthesisDelta: (reviewId, attributeId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/unfinalize-attribute-synthesis-delta/${attributeId}`,
            method: 'POST',
            ...params,
        }),
        reviewsControllerSynthesizeDataExtractionDelta: (reviewId, attributeId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/synthesize-data-extraction-delta/${attributeId}`,
            method: 'POST',
            ...params,
        }),
        reviewsControllerUpdateReviewVersionName: (reviewId, data, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/version-name`,
            method: 'PUT',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewsControllerAcceptAllScreeningSuggestions: (reviewId, params = {}) => this.request({
            path: `/api/Reviews/${reviewId}/accept-all-screening-suggestions`,
            method: 'PUT',
            ...params,
        }),
        usersControllerCreate: (data, params = {}) => this.request({
            path: `/api/users`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        usersControllerFind: (params = {}) => this.request({
            path: `/api/users`,
            method: 'GET',
            format: 'json',
            ...params,
        }),
        usersControllerInviteUser: (data, params = {}) => this.request({
            path: `/api/users/invite`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        usersControllerFindCurrentUser: (params = {}) => this.request({
            path: `/api/users/me`,
            method: 'GET',
            format: 'json',
            ...params,
        }),
        usersControllerUpdateCurrentUserFullName: (data, params = {}) => this.request({
            path: `/api/users/updateFullName`,
            method: 'PATCH',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        usersControllerEditUser: (data, params = {}) => this.request({
            path: `/api/users/edit`,
            method: 'PATCH',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        usersControllerEnableUser: (data, params = {}) => this.request({
            path: `/api/users/enableUser`,
            method: 'PATCH',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        usersControllerDisableUser: (data, params = {}) => this.request({
            path: `/api/users/disableUser`,
            method: 'PATCH',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        usersControllerGetRecoveryLink: (params = {}) => this.request({
            path: `/api/users/get-recovery-link`,
            method: 'POST',
            format: 'json',
            ...params,
        }),
        usersControllerAcceptTerms: (version, params = {}) => this.request({
            path: `/api/users/me/accept-terms/${version}`,
            method: 'POST',
            ...params,
        }),
        usersControllerGetNonPrivilegedUserQuota: (params = {}) => this.request({
            path: `/api/users/quotas/non-privileged`,
            method: 'GET',
            format: 'json',
            ...params,
        }),
        cslStylesControllerFindCslStyles: (params = {}) => this.request({
            path: `/api/csl-styles`,
            method: 'GET',
            format: 'json',
            ...params,
        }),
        healthControllerCheck: (params = {}) => this.request({
            path: `/api/health`,
            method: 'GET',
            format: 'json',
            ...params,
        }),
        reviewItemsControllerAnswerStudyQuestion: (studyId, attributeId, params = {}) => this.request({
            path: `/api/review-items/${studyId}/answer-question/${attributeId}`,
            method: 'POST',
            ...params,
        }),
        reviewItemsControllerAddHighlight: (reviewItemId, data, params = {}) => this.request({
            path: `/api/review-items/${reviewItemId}/add-attribute-highlight`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewItemsControllerGetAttributeHighlights: (reviewItemId, attributeId, params = {}) => this.request({
            path: `/api/review-items/${reviewItemId}/attributes/${attributeId}/highlights`,
            method: 'GET',
            format: 'json',
            ...params,
        }),
        reviewItemsControllerDeleteHighlight: (reviewItemId, data, params = {}) => this.request({
            path: `/api/review-items/${reviewItemId}/highlight`,
            method: 'DELETE',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewItemsControllerAddScreeningHighlight: (reviewItemId, data, params = {}) => this.request({
            path: `/api/review-items/${reviewItemId}/add-screening-highlight`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewItemsControllerGetScreeningHighlights: (reviewItemId, params = {}) => this.request({
            path: `/api/review-items/${reviewItemId}/screening-highlights`,
            method: 'GET',
            format: 'json',
            ...params,
        }),
        reviewItemsControllerDeleteScreeningHighlight: (reviewItemId, data, params = {}) => this.request({
            path: `/api/review-items/${reviewItemId}/screening-highlight`,
            method: 'DELETE',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        settingsControllerGetOpenAiKey: (params = {}) => this.request({
            path: `/api/Settings/settings/open-ai-key`,
            method: 'GET',
            format: 'json',
            ...params,
        }),
        settingsControllerSetOpenAiKey: (data, params = {}) => this.request({
            path: `/api/Settings/settings/open-ai-key`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        settingsControllerGetAiModels: (params = {}) => this.request({
            path: `/api/Settings/settings/ai-models`,
            method: 'GET',
            format: 'json',
            ...params,
        }),
        settingsControllerSetAiModels: (data, params = {}) => this.request({
            path: `/api/Settings/settings/ai-models`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        settingsControllerGetDataExtractionPrompt: (params = {}) => this.request({
            path: `/api/Settings/settings/data-extraction-prompt`,
            method: 'GET',
            format: 'json',
            ...params,
        }),
        settingsControllerSetDataExtractionPrompt: (data, params = {}) => this.request({
            path: `/api/Settings/settings/data-extraction-prompt`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        settingsControllerGetDefaultDataExtractionPrompt: (params = {}) => this.request({
            path: `/api/Settings/settings/data-extraction-prompt/default`,
            method: 'GET',
            format: 'json',
            ...params,
        }),
        teamsControllerCreateTeam: (data, params = {}) => this.request({
            path: `/api/teams`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        teamsControllerFindTeams: (params = {}) => this.request({
            path: `/api/teams`,
            method: 'GET',
            format: 'json',
            ...params,
        }),
        assistantsControllerFindConditionNames: (data, params = {}) => this.request({
            path: `/api/assistants/medical-background/condition-names`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        assistantsControllerGenerateLevelOfEvidence: (data, params = {}) => this.request({
            path: `/api/assistants/medical-background/level-of-evidence`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        assistantsControllerDetermineTimeFrame: (data, params = {}) => this.request({
            path: `/api/assistants/medical-background/time-frame`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        assistantsControllerFindDeviceGroupNames: (data, params = {}) => this.request({
            path: `/api/assistants/notified-body/device-group-names`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        assistantsControllerGenerateLevelOfEvidenceForDeviceGroup: (data, params = {}) => this.request({
            path: `/api/assistants/notified-body/level-of-evidence`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        assistantsControllerDetermineTimeFrameForDeviceGroup: (data, params = {}) => this.request({
            path: `/api/assistants/notified-body/time-frame`,
            method: 'POST',
            body: data,
            type: ContentType.Json,
            format: 'json',
            ...params,
        }),
        utilsControllerGetWebsiteTitle: (query, params = {}) => this.request({
            path: `/api/utils/website-title`,
            method: 'GET',
            query: query,
            format: 'json',
            ...params,
        }),
        reviewSeriesControllerUpdateName: (reviewSeriesId, data, params = {}) => this.request({
            path: `/api/ReviewSeries/${reviewSeriesId}/name`,
            method: 'PATCH',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewSeriesControllerUpdateOrder: (reviewSeriesId, data, params = {}) => this.request({
            path: `/api/ReviewSeries/${reviewSeriesId}/order`,
            method: 'PATCH',
            body: data,
            type: ContentType.Json,
            ...params,
        }),
        reviewSeriesControllerRollback: (reviewSeriesId, params = {}) => this.request({
            path: `/api/ReviewSeries/${reviewSeriesId}`,
            method: 'PUT',
            ...params,
        }),
        reviewSeriesControllerDelete: (reviewSeriesId, params = {}) => this.request({
            path: `/api/ReviewSeries/${reviewSeriesId}`,
            method: 'DELETE',
            ...params,
        }),
        jobsControllerGetJobs: (query, params = {}) => this.request({
            path: `/api/Jobs`,
            method: 'GET',
            query: query,
            format: 'json',
            ...params,
        }),
        jobsControllerPauseAutoScreening: (reviewId, params = {}) => this.request({
            path: `/api/Jobs/pause-auto-screening/${reviewId}`,
            method: 'POST',
            format: 'json',
            ...params,
        }),
        jobsControllerResumeAutoScreening: (reviewId, params = {}) => this.request({
            path: `/api/Jobs/resume-auto-screening/${reviewId}`,
            method: 'POST',
            format: 'json',
            ...params,
        }),
        jobsControllerPauseAutoExtraction: (reviewId, params = {}) => this.request({
            path: `/api/Jobs/pause-auto-extraction/${reviewId}`,
            method: 'POST',
            format: 'json',
            ...params,
        }),
        jobsControllerResumeAutoExtraction: (reviewId, params = {}) => this.request({
            path: `/api/Jobs/resume-auto-extraction/${reviewId}`,
            method: 'POST',
            format: 'json',
            ...params,
        }),
    };
}
//# sourceMappingURL=client.js.map